'use client'

import React, { useEffect, useRef, useState } from 'react'

export type SandboxedHtmlEmbedProps = {
  rawCode: string
  title?: string
  className?: string
  minHeight?: number
}

const SCROLL_BRIDGE_SCRIPT = `
<script>
(function() {
  if (typeof window !== 'undefined') {
    window.IntersectionObserver = function(callback) {
      return {
        observe: function(target) {
          if (typeof callback === 'function' && target) {
            setTimeout(function() {
              callback([{ isIntersecting: true, target: target, intersectionRatio: 1 }]);
            }, 60);
          }
        },
        unobserve: function() {},
        disconnect: function() {},
      };
    };
  }

  function triggerInitialVisibility() {
    var reveals = document.querySelectorAll('.reveal');
    reveals.forEach(function(el) {
      el.classList.add('active');
    });
  }

  function handleScrollData(data) {
    if (!data) return;
    var iframeTop = typeof data.iframeTop === 'number' ? data.iframeTop : 0;
    var windowHeight = data.windowHeight || window.innerHeight || 800;
    var iframeHeight = data.iframeHeight || (document.body ? document.body.scrollHeight : 800);

    // 1. Progress bar support
    var progressBar = document.getElementById('progress-bar');
    if (progressBar && iframeHeight > 0) {
      var scrolled = Math.max(0, -iframeTop);
      var total = Math.max(1, iframeHeight - windowHeight);
      var pct = Math.min(100, Math.max(0, (scrolled / total) * 100));
      progressBar.style.width = pct + '%';
    }

    // 2. Reveal animations: activate when element enters viewport
    var reveals = document.querySelectorAll('.reveal:not(.active)');
    reveals.forEach(function(el) {
      var r = el.getBoundingClientRect();
      var topInParent = iframeTop + r.top;
      if (topInParent < windowHeight - 30) {
        el.classList.add('active');
      }
    });
  }

  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'PARENT_SCROLL') {
      handleScrollData(e.data);
    }
  });

  function init() {
    handleScrollData({ iframeTop: 0, windowHeight: window.innerHeight || 800, iframeHeight: document.body ? document.body.scrollHeight : 800 });
    setTimeout(triggerInitialVisibility, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', init);
})();
</script>
`

function injectBridge(html: string): string {
  if (/<head[\s>]/i.test(html)) {
    return html.replace(/<head[\s>]/i, (match) => `${match}${SCROLL_BRIDGE_SCRIPT}`)
  }
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${SCROLL_BRIDGE_SCRIPT}</head>`)
  }
  if (/<\/body>/i.test(html)) {
    return html.replace(/<\/body>/i, `${SCROLL_BRIDGE_SCRIPT}</body>`)
  }
  return SCROLL_BRIDGE_SCRIPT + html
}

function buildSrcDoc(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''

  // If it's already a full HTML document, inject bridge script and return
  if (/<html[\s>]/i.test(trimmed) || /<!DOCTYPE/i.test(trimmed)) {
    return injectBridge(trimmed)
  }

  // Otherwise, wrap in standard HTML shell with responsive viewport and base typography
  const wrapped = `<!DOCTYPE html>
<html lang="vi">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0" />
  <meta name="referrer" content="no-referrer" />
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      background: transparent;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #1f2937;
      line-height: 1.5;
    }
    img, video, canvas { max-width: 100%; height: auto; }
  </style>
</head>
<body>
  ${trimmed}
</body>
</html>`

  return injectBridge(wrapped)
}

export function SandboxedHtmlEmbed({
  rawCode,
  title,
  className = '',
  minHeight = 80,
}: SandboxedHtmlEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [frameHeight, setFrameHeight] = useState<number>(minHeight)

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    let ro: ResizeObserver | null = null

    const syncHeight = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document
        if (!doc) return

        const body = doc.body
        const html = doc.documentElement
        if (!body || !html) return

        const scrollHeight = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        )

        if (scrollHeight > 0) {
          setFrameHeight(Math.max(minHeight, scrollHeight + 4))
        }
      } catch {
        // Fallback for sandboxed restrictions
      }
    }

    const syncScroll = () => {
      if (!iframe.contentWindow) return
      try {
        const rect = iframe.getBoundingClientRect()
        iframe.contentWindow.postMessage(
          {
            type: 'PARENT_SCROLL',
            iframeTop: rect.top,
            iframeHeight: iframe.offsetHeight,
            windowHeight: window.innerHeight,
            scrollY: window.scrollY,
          },
          '*'
        )
      } catch {
        // ignore
      }
    }

    const onLoad = () => {
      syncHeight()
      syncScroll()
      try {
        const doc = iframe.contentDocument
        if (doc && doc.body && typeof ResizeObserver !== 'undefined') {
          ro = new ResizeObserver(() => syncHeight())
          ro.observe(doc.body)
          ro.observe(doc.documentElement)
        }
      } catch {
        // ignore
      }
    }

    iframe.addEventListener('load', onLoad)
    window.addEventListener('scroll', syncScroll, { passive: true })
    window.addEventListener('resize', syncScroll, { passive: true })

    const timer = setTimeout(() => {
      syncHeight()
      syncScroll()
    }, 150)
    const timer2 = setTimeout(() => {
      syncHeight()
      syncScroll()
    }, 600)
    const timer3 = setTimeout(() => {
      syncHeight()
      syncScroll()
    }, 1500)

    return () => {
      iframe.removeEventListener('load', onLoad)
      window.removeEventListener('scroll', syncScroll)
      window.removeEventListener('resize', syncScroll)
      clearTimeout(timer)
      clearTimeout(timer2)
      clearTimeout(timer3)
      if (ro) {
        ro.disconnect()
      }
    }
  }, [rawCode, minHeight])

  const srcDoc = buildSrcDoc(rawCode)

  return (
    <div
      className={`w-full overflow-hidden ${
        title
          ? 'my-6 first:mt-0 rounded-xl border border-gray-200/80 bg-white shadow-xs transition-shadow hover:shadow-md'
          : 'my-0 border-0 bg-transparent'
      } ${className}`}
    >
      {title && (
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/80 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-charcoal-700">
            {title}
          </span>
          <span className="inline-flex items-center rounded-sm bg-gray-200/70 px-1.5 py-0.5 font-mono text-[10px] font-medium text-charcoal-600">
            Interactive Embed
          </span>
        </div>
      )}
      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-modals"
        className="w-full border-0 transition-[height] duration-200 ease-out"
        style={{ height: `${frameHeight}px`, display: 'block', width: '100%' }}
        title={title || 'Embedded Content'}
        loading="lazy"
      />
    </div>
  )
}

/**
 * Hook to scan a container element (like the blog article body) and
 * dynamically mount Sandboxed iframes inside elements with .raw-html-embed[data-raw-html-embed].
 */
export function useEnhanceRawEmbeds(containerRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const embedElements = container.querySelectorAll<HTMLElement>('.raw-html-embed[data-raw-html-embed]')
    if (embedElements.length === 0) return

    const cleanupFns: (() => void)[] = []

    embedElements.forEach((el) => {
      if (el.dataset.embedEnhanced === 'true') return
      el.dataset.embedEnhanced = 'true'

      const encoded = el.getAttribute('data-raw-html-embed') || ''
      let rawCode = ''
      try {
        rawCode = decodeURIComponent(encoded)
      } catch {
        rawCode = encoded
      }

      const title = el.getAttribute('data-embed-title') || ''
      if (!rawCode.trim()) return

      const isFirst = el === container.firstElementChild || !el.previousElementSibling
      el.innerHTML = ''
      el.style.display = 'block'
      el.style.width = '100%'
      el.style.marginTop = isFirst ? '0px' : '1.5rem'
      el.style.marginBottom = '1.5rem'

      const wrapper = document.createElement('div')
      wrapper.className = title
        ? 'w-full overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs transition-shadow hover:shadow-md'
        : 'w-full overflow-hidden border-0 bg-transparent'

      if (title) {
        const header = document.createElement('div')
        header.className =
          'flex items-center justify-between border-b border-gray-100 bg-gray-50/80 px-4 py-2.5'
        header.innerHTML = `
          <span class="text-xs font-semibold uppercase tracking-wider text-charcoal-700">${title}</span>
          <span class="inline-flex items-center rounded-sm bg-gray-200/70 px-1.5 py-0.5 font-mono text-[10px] font-medium text-charcoal-600">Interactive Embed</span>
        `
        wrapper.appendChild(header)
      }

      const iframe = document.createElement('iframe')
      iframe.sandbox.add('allow-scripts', 'allow-forms', 'allow-same-origin', 'allow-popups', 'allow-modals')
      iframe.className = 'w-full border-0 transition-[height] duration-200 ease-out'
      iframe.style.width = '100%'
      iframe.style.display = 'block'
      iframe.style.height = '120px'
      iframe.srcdoc = buildSrcDoc(rawCode)
      iframe.title = title || 'Interactive Embed'

      let ro: ResizeObserver | null = null

      const syncHeight = () => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document
          if (!doc) return
          const body = doc.body
          const html = doc.documentElement
          if (!body || !html) return

          const scrollHeight = Math.max(
            body.scrollHeight,
            body.offsetHeight,
            html.clientHeight,
            html.scrollHeight,
            html.offsetHeight
          )

          if (scrollHeight > 0) {
            iframe.style.height = `${scrollHeight + 4}px`
          }
        } catch {
          // ignore
        }
      }

      const syncScroll = () => {
        if (!iframe.contentWindow) return
        try {
          const rect = iframe.getBoundingClientRect()
          iframe.contentWindow.postMessage(
            {
              type: 'PARENT_SCROLL',
              iframeTop: rect.top,
              iframeHeight: iframe.offsetHeight,
              windowHeight: window.innerHeight,
              scrollY: window.scrollY,
            },
            '*'
          )
        } catch {
          // ignore
        }
      }

      const onLoad = () => {
        syncHeight()
        syncScroll()
        try {
          const doc = iframe.contentDocument
          if (doc && doc.body && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => syncHeight())
            ro.observe(doc.body)
            ro.observe(doc.documentElement)
          }
        } catch {
          // ignore
        }
      }

      iframe.addEventListener('load', onLoad)
      window.addEventListener('scroll', syncScroll, { passive: true })
      window.addEventListener('resize', syncScroll, { passive: true })

      const t1 = setTimeout(() => {
        syncHeight()
        syncScroll()
      }, 150)
      const t2 = setTimeout(() => {
        syncHeight()
        syncScroll()
      }, 600)
      const t3 = setTimeout(() => {
        syncHeight()
        syncScroll()
      }, 1500)

      wrapper.appendChild(iframe)
      el.appendChild(wrapper)

      cleanupFns.push(() => {
        iframe.removeEventListener('load', onLoad)
        window.removeEventListener('scroll', syncScroll)
        window.removeEventListener('resize', syncScroll)
        clearTimeout(t1)
        clearTimeout(t2)
        clearTimeout(t3)
        if (ro) ro.disconnect()
      })
    })

    return () => {
      cleanupFns.forEach((fn) => fn())
    }
  }, [containerRef])
}
